import packageJson from '../../package.json';
import React, {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

type CacheBusterContextProviderProps = {
    children: ReactNode;
};

type MetaJson = {
    version: string;
};

type CacheBusterContextValue = {
    isUpdateAvailable: boolean;
    refreshCacheAndReload: () => Promise<void>;
    dismissUpdateNotification: () => void;
};

const CURRENT_APP_VERSION = packageJson.version;

const FIRST_VERSION_SEGMENT_INDEX = 0;
const VERSION_SEGMENT_INCREMENT = 1;
const DEFAULT_VERSION_SEGMENT = 0;

const VERSION_CHECK_INTERVAL_MINUTES = 15;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;

const VERSION_CHECK_INTERVAL_MS =
    VERSION_CHECK_INTERVAL_MINUTES *
    SECONDS_PER_MINUTE *
    MILLISECONDS_PER_SECOND;

const CacheBusterContext = createContext<CacheBusterContextValue>({
    isUpdateAvailable: false,
    refreshCacheAndReload: async () => undefined,
    dismissUpdateNotification: () => undefined,
});

const isVersionGreaterThan = (versionA: string, versionB: string): boolean => {
    const versionsA = versionA.split('.').map(Number);
    const versionsB = versionB.split('.').map(Number);
    const maxLength = Math.max(versionsA.length, versionsB.length);

    for (
        let index = FIRST_VERSION_SEGMENT_INDEX;
        index < maxLength;
        index += VERSION_SEGMENT_INCREMENT
    ) {
        const a = versionsA[index] ?? DEFAULT_VERSION_SEGMENT;
        const b = versionsB[index] ?? DEFAULT_VERSION_SEGMENT;

        if (a > b) {
            return true;
        }

        if (a < b) {
            return false;
        }
    }

    return false;
};

const clearBrowserCaches = async (): Promise<void> => {
    if (!('caches' in window)) {
        return;
    }

    const cacheNames = await window.caches.keys();

    await Promise.all(
        cacheNames.map((cacheName) => window.caches.delete(cacheName)),
    );
};

const refreshCacheAndReload = async (): Promise<void> => {
    await clearBrowserCaches();

    window.location.reload();
};

const fetchLatestAppVersion = async (): Promise<string> => {
    const response = await fetch('/meta.json', {
        cache: 'no-store',
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch meta.json. Status: ${response.status}`,
        );
    }

    const meta = (await response.json()) as MetaJson;

    if (!meta.version) {
        throw new Error('meta.json does not contain a version field');
    }

    return meta.version;
};

const CacheBusterContextProvider = ({
    children,
}: CacheBusterContextProviderProps) => {
    const [isCheckingVersion, setIsCheckingVersion] = useState(true);
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const isCheckingRef = useRef(false);
    const isUpdateDismissedRef = useRef(false);

    const checkAppVersion = useCallback(async (): Promise<void> => {
        if (
            isCheckingRef.current ||
            isUpdateAvailable ||
            isUpdateDismissedRef.current
        ) {
            return;
        }

        isCheckingRef.current = true;

        try {
            const latestVersion = await fetchLatestAppVersion();

            const hasNewVersion = isVersionGreaterThan(
                latestVersion,
                CURRENT_APP_VERSION,
            );

            if (hasNewVersion) {
                const isDashboardRoute =
                    window.location.pathname.startsWith('/dashboard');

                console.log(
                    `New app version detected. Current: ${CURRENT_APP_VERSION}. Latest: ${latestVersion}.`,
                );

                if (isDashboardRoute) {
                    setIsUpdateAvailable(true);

                    return;
                }

                await refreshCacheAndReload();

                return;
            }

            console.log(
                `App is up to date. Current: ${CURRENT_APP_VERSION}. Latest: ${latestVersion}.`,
            );
        } catch (error) {
            console.log('Cache buster check failed:', error);
        } finally {
            isCheckingRef.current = false;
            setIsCheckingVersion(false);
        }
    }, [isUpdateAvailable]);

    const dismissUpdateNotification = useCallback((): void => {
        console.log('here');
        isUpdateDismissedRef.current = true;
        setIsUpdateAvailable(false);
    }, []);

    useEffect(() => {
        void checkAppVersion();

        const intervalId = window.setInterval(() => {
            void checkAppVersion();
        }, VERSION_CHECK_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [checkAppVersion]);

    const contextValue = useMemo(
        () => ({
            isUpdateAvailable,
            refreshCacheAndReload,
            dismissUpdateNotification,
        }),
        [isUpdateAvailable],
    );

    return (
        <CacheBusterContext.Provider value={contextValue}>
            {isCheckingVersion ? null : children}
        </CacheBusterContext.Provider>
    );
};

export { CacheBusterContext, CacheBusterContextProvider };
